import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AppService } from '../../../services/app.service';
import { TransactionsService } from '../../../services/transactions.service';
import { UtilsService } from '../../../services/utils.service';
import { AuthService } from '../../../services/auth.service';
import { Roles } from '../../../models/roles';
import { UserService } from '../../../services/users.service';
import { LoggerService } from '../../../services/logger.service';
import { COUNTRY_OPTIONS } from '../../../models/countries';
import { CashoutDraft } from './currencies/cashout-currency.types';
import {
  extractBankName,
  toTitleCase,
} from './currencies/cashout-bank.helpers';
import {
  applyTransactionTypeValidators,
  getFallbackCashoutTransactionType,
  getTransactionTypesForCurrency,
  isBankTransfer,
} from './currencies/cashout-transaction-type.helpers';
import {
  createNotbankBankFormGroup,
  isNotbankBankSelected,
  NOTBANK_FIAT_CURRENCIES,
} from './currencies/notbank-cashout.logic';
import { NotbankService } from '../../../services/notbank.service';
import { ProviderServiceService } from '../../../services/provider_service.service';
import {
  extractNotbankErrorBody,
  isNotbankAccountProvisioned,
  isNotbankProvisionRequiredError,
} from '../notbank-provision/notbank-provision.logic';

@Component({
  selector: 'app-add-cashout',
  templateUrl: './add-cashout.component.html',
  styleUrl: './add-cashout.component.scss',
})
export class AddCashoutsComponent implements OnInit, OnDestroy {
  private readonly csvUploadCurrencies = new Set([
    'VES',
    'COP',
    'EUR',
    'AED',
    'USD',
    'MXN',
    'BTC',
    'ETH',
    'USDT',
  ]);

  @Input() currency: string = '';
  @Input() immediateCreditOnly = false;
  currencyPicker: any = null;

  roles = Roles;
  countryOptions = COUNTRY_OPTIONS;
  transactionTypes: Array<{ currency: string; id: string }> = [];
  transaction: any;
  showTransactionModal = false;

  showNewTransaction = false;
  showNewTransactionType = false;
  newTransactionType: null | string = null;
  addingTransaction = false;
  r4CreditDestination: 'phone' | 'account' = 'phone';
  showCashoutConfirmation = false;
  pendingCashoutBody: any = null;
  cashoutConfirmationDetails: Array<{ label: string; value: string }> = [];

  newTransactionForm = this.formBuilder.group({
    transaction_id: [''],
    time_zone: ['GMT', Validators.required],
    firstname: ['', Validators.required],
    lastname: ['', Validators.required],
    id_type: [''],
    id_number: [''],
    transaction_type: ['', Validators.required],
    company_name: ['', Validators.required],
    company_id: ['', Validators.required],
    account_type: [''],
    account_number: ['', Validators.required],
    amount: ['', Validators.required],
    description: [''],
    client_id: [
      !this.auth.hasRole(Roles.Admin) ? this.auth.user.user_id : null,
      Validators.required,
    ],
    currency: [this.currency, Validators.required],
    type: ['cashout'],
    /** COP: documento en formato tipo-número (ej. CC-123456789); se hace split por guion al enviar. */
    document_type_number: [''],
    minteo_origin_wallet_id: [null as string | null],
    /** Minteo CO: SAVE | TURBO | BREB */
    minteo_channel: ['SAVE'],
    breb_key_type: [null as string | null],
    breb_key_value: [''],
    /** COP Bitso: banco real del beneficiario (código ACH); company_id = BITSO_COP */
    cop_beneficiary_bank: [null as string | null],
    clearjunction_payee_type: ['individual'],
    clearjunction_payee: this.formBuilder.group({
      address: this.formBuilder.group({
        country: [''],
        zip: [''],
        city: [''],
        street: [''],
      }),
    }),
    fuse_payout: this.formBuilder.group({
      account_id: [''],
      purpose: ['salaries'],
      external_reference: [''],
      rail: ['iban'],
      iban: [''],
      wire_account: [''],
      wire_routing: [''],
      bank_bic: [''],
      address_country_code: [''],
      address_line_1: [''],
      beneficiary_type: ['individual'],
    }),
    /** Bitso MXN SPEI — ver https://docs.bitso.com/bitso-payouts-funding/docs/mxn-withdrawals */
    bitso_mxn: this.formBuilder.group({
      protocol: ['clabe'],
      clabe: [''],
      rfc: [''],
      institution_code: [''],
      notes_ref: ['', Validators.maxLength(40)],
    }),
    notbank_bank: createNotbankBankFormGroup(this.formBuilder),
  });

  selectedFile: File | null = null;
  uploadingCSV = false;

  showCurrency = false;
  readonly cashoutContext = this;

  showNotbankProvision = false;
  notbankProvisionClientId: number | string | null = null;
  notbankProvisionMissing: string[] = [];
  notbankProvisionPartial: Record<string, unknown> | null = null;
  notbankProvisionBank: Record<string, unknown> | null = null;
  notbankProvisionError = '';
  private pendingDraftAfterProvision: CashoutDraft | null = null;

  constructor(
    private transactionsService: TransactionsService,
    private formBuilder: FormBuilder,
    public utilsService: UtilsService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    public auth: AuthService,
    public appService: AppService,
    public userService: UserService,
    private activatedRoute: ActivatedRoute,
    private logger: LoggerService,
    private notbankService: NotbankService,
    private providerServicesService: ProviderServiceService,
  ) {}

  ngOnInit() {
    if (this.immediateCreditOnly) {
      this.currency = 'VES';
    }
    this.openNewTransaction();
    if (this.immediateCreditOnly) {
      this.continueImmediateCredit();
      return;
    }
    if (!this.currencyPicker) {
      this.showCurrency = true;
      this.showNewTransactionType = true;
      return;
    }
    this.continue();
  }

  ngOnDestroy(): void {}

  private dismissAddCashoutModal(): void {
    this.appService.triggerAddCashout(false);
    this.showNewTransactionType = false;
    this.showNewTransaction = false;
    this.showCashoutConfirmation = false;
    this.pendingCashoutBody = null;
    this.cashoutConfirmationDetails = [];
  }

  selectCurrency(currency: any) {
    const selected = currency?.value ?? currency;
    this.currency = this.extractCurrencyId(selected);

    if (!this.isCashoutCurrencyAllowed(this.currency)) {
      this.currency = '';
      this.currencyPicker = null;
      this.transactionTypes = [];
      this.setTransactionType(null);
      return;
    }

    this.currencyPicker = selected;

    this.newTransactionForm.controls.currency.setValue(this.currency || null);

    this.transactionTypes = this.getTransactionTypesForCurrency(this.currency);
    this.applyTransactionTypeValidation();

    if (this.transactionTypes.length) {
      this.setTransactionType(this.transactionTypes[0].id);
    } else {
      this.setTransactionType(null);
    }

    this.setCashoutType('normal');
  }

  resetForm() {
    this.newTransactionForm = this.formBuilder.group({
      transaction_id: [''],
      time_zone: ['GMT', Validators.required],
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      id_type: ['V'],
      id_number: [''],
      transaction_type: [this.transactionTypes[0]?.id ?? null],
      company_name: ['', Validators.required],
      company_id: ['', Validators.required],
      account_type: [''],
      account_number: ['', Validators.required],
      amount: ['', Validators.required],
      description: [''],
      client_id: [
        !this.auth.hasRole(Roles.Admin) ? this.auth.user.user_id : null,
        Validators.required,
      ],
      currency: [this.currency, Validators.required],
      type: ['cashout'],
      document_type_number: [''],
      minteo_origin_wallet_id: [null as string | null],
      minteo_channel: ['SAVE'],
      breb_key_type: [null as string | null],
      breb_key_value: [''],
      cop_beneficiary_bank: [null as string | null],
      clearjunction_payee_type: ['individual'],
      clearjunction_payee: this.formBuilder.group({
        address: this.formBuilder.group({
          country: [''],
          zip: [''],
          city: [''],
          street: [''],
        }),
      }),
      fuse_payout: this.formBuilder.group({
        account_id: [''],
        purpose: ['salaries'],
        external_reference: [''],
        rail: ['iban'],
        iban: [''],
        wire_account: [''],
        wire_routing: [''],
        bank_bic: [''],
        address_country_code: [''],
        address_line_1: [''],
        beneficiary_type: ['individual'],
      }),
      bitso_mxn: this.formBuilder.group({
        protocol: ['clabe'],
        clabe: [''],
        rfc: [''],
        institution_code: [''],
        notes_ref: ['', Validators.maxLength(40)],
      }),
      notbank_bank: createNotbankBankFormGroup(this.formBuilder),
    });
  }

  openNewTransaction = () => {
    if (this.currency && !this.isCashoutCurrencyAllowed(this.currency)) {
      this.currency = '';
    }

    this.transactionTypes = this.getTransactionTypesForCurrency(this.currency);

    this.showNewTransactionType = false;
    this.newTransactionType = 'normal';
    this.newTransactionForm.reset();

    this.resetForm();

    this.currencyPicker = this.allowedCashoutCurrencies.find(
      (el: any) => el.id === this.currency,
    );

    this.applyTransactionTypeValidation();
  };

  getBanks() {
    if (this.currency === 'VES') {
      return (
        this.appService.config?.currencies?.find(
          (el: any) => (el.id ?? el.currency) === this.currency,
        )?.subwallets ?? []
      );
    }

    return this.userService.user_balance_config.currencies.find(
      (el: any) => el.id === this.currency,
    )?.subwallets;
  }

  exitNewTransaction = () => {
    this.dismissAddCashoutModal();
  };

  setCashoutType = (type: string) => {
    this.newTransactionType = type;
  };

  showCsvUpload(): void {
    if (!this.canShowCsvUpload()) {
      return;
    }
    this.selectedFile = null;
    this.newTransactionType = 'csv';
  }

  canShowCsvUpload(): boolean {
    return (
      !this.immediateCreditOnly && this.csvUploadCurrencies.has(this.currency)
    );
  }

  showCashoutForm(): void {
    this.selectedFile = null;
    this.newTransactionType = 'normal';
  }

  private continueImmediateCredit() {
    this.currency = 'VES';
    this.currencyPicker =
      this.allowedCashoutCurrencies.find((el: any) => el.id === 'VES') || {
        id: 'VES',
        shortname: 'VES',
      };
    this.newTransactionForm.controls.currency.setValue('VES');
    this.transactionTypes = [{ id: 'Crédito inmediato', currency: 'VES' }];
    this.setTransactionType('Crédito inmediato');
    this.applyTransactionTypeValidation();
    this.showNewTransactionType = false;
    this.showNewTransaction = true;
  }

  continue() {
    if (this.checkContinueDisabled()) return;

    this.transactionTypes = this.getTransactionTypesForCurrency(this.currency);

    if (!this.transactionTypes.length) {
      this.logger.log(
        '[Cashouts] No se encontraron tipos de transacción para la moneda seleccionada. Se habilita flujo genérico.',
      );
      this.setTransactionType(getFallbackCashoutTransactionType(this.currency));
    } else {
      this.setTransactionType(this.transactionTypes[0]?.id ?? null);
    }

    this.applyTransactionTypeValidation();

    this.showNewTransactionType = false;
    this.showNewTransaction = true;
  }

  setDocuType(type: string) {
    this.newTransactionForm.controls.id_type.setValue(type);
  }

  setAccountType(type: string) {
    this.newTransactionForm.controls.account_type.setValue(type);
  }

  setTransactionType(type: string | null) {
    this.newTransactionForm.controls.transaction_type.setValue(type ?? null);
    if (!this.isImmediateCreditType(type)) {
      this.r4CreditDestination = 'phone';
    }
  }

  setR4CreditDestination(destination: 'phone' | 'account') {
    this.r4CreditDestination = destination;
  }

  isImmediateCreditType(value: any): boolean {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase()
      .includes('credito inmediato');
  }

  get isR4ImmediateCredit(): boolean {
    return (
      this.currency === 'VES' &&
      this.isImmediateCreditType(
        this.newTransactionForm?.controls?.transaction_type?.value,
      )
    );
  }

  get r4CashoutProviderServiceId(): number | null {
    const services = this.providerServicesService?.providerServices ?? [];
    const row = services.find((el: any) => {
      const username = String(
        el?.provider?.username || el?.username || '',
      ).toUpperCase();
      const providerId = Number(
        el?.provider?.user_id ?? el?.provider?.id ?? el?.provider_id,
      );
      return (
        el?.active &&
        String(el?.service_id || '').toLowerCase() === 'cashout' &&
        String(el?.origin_coin_id || '').toUpperCase() === 'VES' &&
        (username === 'R4' || providerId === 15)
      );
    });
    return row?.id ?? null;
  }

  openCashoutConfirmationFromDraft(draft: CashoutDraft): void {
    const ccy = String(this.currency || '').toUpperCase();
    const isNotbankCcy = (NOTBANK_FIAT_CURRENCIES as readonly string[]).includes(
      ccy,
    );
    const needsNotbank =
      isNotbankCcy &&
      (isNotbankBankSelected(this) ||
        !!(draft.body as any)?.notbank_bank_account ||
        String((draft.body as any)?.company_id || '')
          .toUpperCase()
          .includes('NOTBANK'));

    if (!needsNotbank) {
      this.pendingCashoutBody = draft.body;
      this.cashoutConfirmationDetails = draft.confirmationDetails;
      this.showCashoutConfirmation = true;
      return;
    }

    const clientRaw =
      (draft.body as any)?.client_id ??
      this.newTransactionForm.controls.client_id.value;
    const clientId =
      typeof clientRaw === 'object' && clientRaw
        ? clientRaw.user_id ?? clientRaw.id
        : clientRaw;

    if (!clientId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Cliente',
        detail: 'Selecciona un cliente para cashout Notbank.',
      });
      return;
    }

    this.notbankService.getAccount(clientId).subscribe({
      next: (resp) => {
        if (isNotbankAccountProvisioned(resp)) {
          this.pendingCashoutBody = draft.body;
          this.cashoutConfirmationDetails = draft.confirmationDetails;
          this.showCashoutConfirmation = true;
          return;
        }
        this.openNotbankProvisionForCashout(draft, clientId, {
          message: 'El cliente no tiene cuenta hijo Notbank. Completa el alta.',
        });
      },
      error: (err) => {
        if (
          err?.status === 404 ||
          err?.error?.status === 404 ||
          isNotbankProvisionRequiredError(err)
        ) {
          this.openNotbankProvisionForCashout(
            draft,
            clientId,
            {
              ...extractNotbankErrorBody(err),
              message:
                extractNotbankErrorBody(err)['message'] ||
                'El cliente no tiene cuenta hijo Notbank. Completa el alta.',
            },
          );
          return;
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Notbank',
          detail:
            extractNotbankErrorBody(err)['message'] ||
            err?.message ||
            'No se pudo verificar la cuenta Notbank del cliente.',
        });
      },
    });
  }

  private openNotbankProvisionForCashout(
    draft: CashoutDraft,
    clientId: any,
    errBody?: Record<string, any>,
  ): void {
    this.pendingDraftAfterProvision = draft;
    this.notbankProvisionClientId = clientId;
    this.notbankProvisionMissing = Array.isArray(errBody?.['missing_fields'])
      ? errBody!['missing_fields']
      : [];
    this.notbankProvisionPartial =
      errBody?.['partial_register'] &&
      typeof errBody['partial_register'] === 'object'
        ? errBody['partial_register']
        : null;
    this.notbankProvisionBank =
      ((draft.body as any)?.notbank_bank_account as Record<string, unknown>) ||
      null;
    this.notbankProvisionError =
      errBody?.['message'] ||
      'Provisiona la cuenta hijo Notbank para continuar el cashout.';
    this.showNotbankProvision = true;
  }

  onNotbankProvisioned(_result: any): void {
    this.showNotbankProvision = false;
    const draft = this.pendingDraftAfterProvision;
    this.pendingDraftAfterProvision = null;
    if (draft) {
      this.pendingCashoutBody = draft.body;
      this.cashoutConfirmationDetails = draft.confirmationDetails;
      this.showCashoutConfirmation = true;
    }
  }

  onNotbankProvisionCancelled(): void {
    this.showNotbankProvision = false;
    this.pendingDraftAfterProvision = null;
  }

  backToCashoutForm(): void {
    this.showCashoutConfirmation = false;
  }

  confirmCashout(): void {
    if (!this.pendingCashoutBody || this.addingTransaction) {
      return;
    }

    this.addingTransaction = true;
    this.addTransaction(this.pendingCashoutBody);
  }

  addTransaction(body: any) {
    if (!this.auth.guardTransactionalAction()) return;
    const row = Array.isArray(body) ? body[0] : body;
    if (row?.r4_immediate_credit) {
      this.transactionsService.r4ImmediateCredit(row).subscribe(
        (resp) => {
          this.addingTransaction = false;
          const status = resp?.data?.status;
          if (resp?.success === false || status === 'rejected') {
            this.messageService.add({
              severity: 'error',
              summary: 'Crédito inmediato',
              detail: resp?.message || 'R4 rechazó el crédito.',
            });
            return;
          }
          this.messageService.add({
            severity: status === 'finished' ? 'success' : 'info',
            summary:
              status === 'finished' ? 'Cashout finished' : 'Cashout en validación',
            detail:
              resp?.message ||
              (status === 'finished'
                ? 'El proveedor confirmó el crédito.'
                : 'El cashout quedó en VES. Aún no recibimos 200 AC00.'),
          });
          this.showCashoutConfirmation = false;
          this.pendingCashoutBody = null;
          this.appService.triggerTransactionCreated('cashout');
          this.dismissAddCashoutModal();
        },
        (err) => {
          this.addingTransaction = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Crédito inmediato',
            detail:
              err?.error?.message ||
              err?.message ||
              'No se pudo registrar el crédito inmediato.',
          });
        },
      );
      return;
    }
    this.transactionsService.addTransaction(body).subscribe(
      (resp) => {
        if (resp.status !== 'Error') {
          this.messageService.add({
            severity: 'success',
            summary: 'Listo',
            detail: 'Hemos agregado el cashout correctamente.',
          });
          this.addingTransaction = false;
          this.showCashoutConfirmation = false;
          this.pendingCashoutBody = null;
          this.appService.triggerTransactionCreated('cashout');
          this.dismissAddCashoutModal();
        } else {
          this.addingTransaction = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Ocurrió un error al agregar el cashout. ${resp.message}`,
          });
        }
      },
      (err) => {
        this.logger.log(err);
        this.addingTransaction = false;

        if (isNotbankProvisionRequiredError(err)) {
          const body = this.pendingCashoutBody || {};
          const clientRaw = (body as any).client_id;
          const clientId =
            typeof clientRaw === 'object' && clientRaw
              ? clientRaw.user_id ?? clientRaw.id
              : clientRaw;
          this.openNotbankProvisionForCashout(
            {
              body: body as any,
              confirmationDetails: this.cashoutConfirmationDetails,
            } as CashoutDraft,
            clientId,
            extractNotbankErrorBody(err),
          );
          return;
        }

        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Ocurrió un error al agregar el cashout. ${
            err?.error?.message || err.message
          }`,
        });
      },
    );
  }

  pickBank(e: any) {
    const selected = e?.value ?? e;
    const name = extractBankName(selected);
    const resolvedName = name || toTitleCase(extractBankName(selected));
    this.newTransactionForm.controls.company_name.setValue(resolvedName);

    if (this.isR4ImmediateCredit) {
      return;
    }
    const types = this.getTransactionTypesForCurrency(this.currency);
    if (types.length) {
      this.setTransactionType(types[0]?.id ?? null);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Verifica si es un CSV por seguridad extra
      if (file.name.endsWith('.csv')) {
        this.selectedFile = file;
      } else {
        alert('Solo se permiten archivos .csv');
        this.selectedFile = null;
      }
    }
  }

  uploadFile(): void {
    if (!this.selectedFile || this.uploadingCSV) return;
    if (!this.canShowCsvUpload()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Subida no disponible',
        detail: 'La subida en lotes no está disponible para esta moneda.',
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', this.selectedFile);
    formData.append('currency', this.currency);

    this.uploadingCSV = true;
    this.transactionsService.uploadCSV(formData).subscribe({
      next: (res) => {
        this.uploadingCSV = false;

        this.messageService.add({
          severity: 'success',
          summary: 'Listo',
          detail: 'Hemos subido tu .csv correctamente.',
        });

        this.exitNewTransaction();
      },
      error: (err) => {
        this.uploadingCSV = false;

        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Ocurrió un error al subir tu archivo .csv`,
        });
      },
    });
  }

  closeAddModal = () => {
    this.dismissAddCashoutModal();
  };

  checkContinueDisabled = () => {
    if (!this.newTransactionType || !this.currencyPicker) {
      return true;
    }

    if (!this.isCashoutCurrencyAllowed(this.currency)) {
      return true;
    }

    return false;
  };

  get allowedCashoutCurrencies(): any[] {
    return this.userService.getPlatformServiceAllowedCurrencies('cashout');
  }

  get isBankTransferSelected(): boolean {
    const value =
      this.newTransactionForm?.controls?.transaction_type?.value ?? null;
    return isBankTransfer(value);
  }

  get accountNumberLabel(): string {
    if (this.isR4ImmediateCredit && this.r4CreditDestination === 'account') {
      return 'Número de cuenta';
    }
    return this.isBankTransferSelected
      ? 'Número de cuenta'
      : 'Número de teléfono';
  }

  private extractCurrencyId(option: any): string {
    if (!option) {
      return '';
    }

    if (typeof option === 'string') {
      return option.trim().toUpperCase();
    }

    if (typeof option === 'object') {
      const { id, currency, code } = option as {
        id?: string;
        currency?: string;
        code?: string;
      };

      const raw = id ?? currency ?? code;
      if (raw) {
        return raw.toString().trim().toUpperCase();
      }
    }

    return option.toString().trim().toUpperCase();
  }

  private isCashoutCurrencyAllowed(currency: any): boolean {
    return this.userService.isPlatformServiceCurrencyAllowed('cashout', currency);
  }

  private applyTransactionTypeValidation() {
    const control = this.newTransactionForm.controls.transaction_type;
    if (!control) {
      return;
    }

    applyTransactionTypeValidators(control, this.transactionTypes);
  }

  private getTransactionTypesForCurrency(currency: string): any[] {
    const types = this.userService.user_balance_config?.transaction_types ?? [];
    const matched = getTransactionTypesForCurrency(types, currency);
    if (this.immediateCreditOnly) {
      return [{ id: 'Crédito inmediato', currency: 'VES' }];
    }
    return matched.filter((type: any) => !this.isImmediateCreditType(type?.id));
  }
}
